/**
 * Billing service — starts Stripe checkout / opens the customer portal via the
 * gateway `service=billing` route (ROADMAP Epic 0.1). The client sends only the
 * plan *name*; price IDs live server-side. No card data ever touches this app —
 * checkout happens on Stripe's hosted page.
 */
import { auth } from '../firebase';
import { gatewayFetch, type GatewayAuth } from './gateway';

export type PaidPlan = 'signals' | 'strategist';
export type BillingInterval = 'month' | 'year';

/**
 * Which plans can be billed yearly at 20% off (ROADMAP 0.R R7). A yearly price
 * exists only once it is set up, so the page asks before offering; any failure
 * answers "none" — the toggle simply does not appear.
 */
export async function fetchYearlyCatalog(): Promise<Record<PaidPlan, boolean>> {
  const none = { signals: false, strategist: false };
  try {
    const res = await gatewayFetch('billing', 'catalog', { auth: await billingAuth() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.yearly) return none;
    return { signals: data.yearly.signals === true, strategist: data.yearly.strategist === true };
  } catch {
    return none;
  }
}

/** The sign-in token when signed in, nothing when not — and a token that
 *  cannot be read throws, as it always has: a payment call never goes out
 *  half-authenticated. */
async function billingAuth(): Promise<GatewayAuth> {
  const user = auth.currentUser;
  if (!user) return 'none';
  return { token: await user.getIdToken() };
}

/**
 * Start a subscription checkout for `plan` and redirect the browser to the
 * Stripe hosted-checkout page. Throws with a user-facing message on failure.
 */
export async function createCheckout(plan: PaidPlan, interval: BillingInterval = 'month'): Promise<void> {
  const res = await gatewayFetch('billing', 'create-checkout', {
    auth: await billingAuth(),
    body: { plan, interval },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.checkoutUrl) {
    throw new Error(data.error || 'Could not start checkout. Please try again.');
  }
  window.location.assign(data.checkoutUrl);
}

/**
 * Open the Stripe customer portal (manage or cancel a subscription).
 *
 * In a NEW TAB (owner, 2026-08-23): the portal is a different system, and
 * navigating away mid-session loses whatever the reader was doing on the plans
 * page. It also means they come back to a page that is still theirs rather
 * than to a Stripe redirect.
 *
 * The tab is opened SYNCHRONOUSLY, before the await. A `window.open` that
 * happens after a network round trip is no longer attributable to the click,
 * and every popup blocker refuses it — the classic version of this bug is a
 * button that works for the developer (blocker off) and silently does nothing
 * for the user. The blank tab is closed again if the URL never arrives, so a
 * failure does not leave an orphan tab behind.
 */
export async function openBillingPortal(): Promise<void> {
  const tab = window.open('', '_blank', 'noopener,noreferrer');
  try {
    const res = await gatewayFetch('billing', 'portal', { auth: await billingAuth(), method: 'POST' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.portalUrl) {
      throw new Error(data.error || 'Could not open the billing portal.');
    }
    if (tab) tab.location.href = data.portalUrl;
    else window.location.assign(data.portalUrl);   // blocker won: same-tab fallback
  } catch (e) {
    tab?.close();
    throw e;
  }
}

export interface CancelState {
  cancelAtPeriodEnd: boolean;
  /** Epoch seconds from the backend; null when Stripe did not say. */
  currentPeriodEnd: number | null;
}

/**
 * Stop the subscription at the end of the current cycle (ROADMAP 0.9). Nothing
 * is refunded and nothing ends early — access runs until the period end the
 * result names. Throws with a user-facing message on failure.
 */
export async function cancelSubscription(): Promise<CancelState> {
  const res = await gatewayFetch('billing', 'cancel-subscription', { auth: await billingAuth(), method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Could not cancel the subscription. Please try again.');
  }
  return { cancelAtPeriodEnd: true, currentPeriodEnd: data.currentPeriodEnd ?? null };
}

/** Undo a pending cancellation before the period ends. */
export async function resumeSubscription(): Promise<CancelState> {
  const res = await gatewayFetch('billing', 'resume-subscription', { auth: await billingAuth(), method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Could not resume the subscription. Please try again.');
  }
  return { cancelAtPeriodEnd: false, currentPeriodEnd: data.currentPeriodEnd ?? null };
}

/**
 * Schedule a Strategist -> Signals switch at the end of the current cycle
 * (ROADMAP 0.11). Nothing changes until then — the plan already paid for runs
 * its full period. Returns when the switch takes effect (epoch seconds).
 */
export async function downgradeSubscription(): Promise<{ effectiveAt: number | null }> {
  const res = await gatewayFetch('billing', 'downgrade-subscription', { auth: await billingAuth(), method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Could not schedule the downgrade. Please try again.');
  }
  return { effectiveAt: data.effectiveAt ?? null };
}

/**
 * Move an existing Signals subscription up to Strategist ([[BUGS #200]]).
 *
 * **Not checkout.** Checkout in subscription mode creates a NEW subscription,
 * so sending a paying customer through it left them holding two and paying for
 * both — while the webhook's last event made the product look correct. This
 * modifies the subscription that already exists: effective immediately, with
 * the difference prorated for the rest of the period they have paid for.
 *
 * Throws `NO_SUBSCRIPTION` when the caller has nothing to modify — that is a
 * genuinely new customer, and the caller should send them to checkout instead
 * of showing them an error.
 */
export async function upgradeSubscription(): Promise<{ plan: string }> {
  const res = await gatewayFetch('billing', 'upgrade-subscription', { auth: await billingAuth(), method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || 'Could not change your plan. Please try again.');
    (err as Error & { code?: string }).code = data.error_code;
    throw err;
  }
  return { plan: data.plan ?? 'strategist' };
}

/** Undo a pending downgrade — keep Strategist. */
export async function cancelDowngrade(): Promise<void> {
  const res = await gatewayFetch('billing', 'cancel-downgrade', { auth: await billingAuth(), method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Could not keep the plan. Please try again.');
  }
}

/**
 * Redeem a trial code (ROADMAP 0.R R6): the plan for the code's days, no card.
 * A refusal throws with the server's sentence, which is written for the reader.
 */
export async function redeemCode(code: string): Promise<{ plan: string; endsAt: string }> {
  const res = await gatewayFetch('billing', 'redeem-code', {
    auth: await billingAuth(),
    body: { code },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.plan) {
    throw new Error(data.error || 'Could not redeem the code right now. Please try again.');
  }
  return { plan: data.plan, endsAt: data.endsAt };
}

/**
 * Give up complimentary access (ROADMAP 2.10).
 *
 * The half of "cancel" Stripe cannot serve: a comped user has no subscription
 * and no portal, so without this the Cancel button offered to end something
 * they never had. Immediate — there is no paid period to run down — and a
 * subscription underneath is left alone.
 */
export async function endComplimentaryAccess(): Promise<{ plan: string; endedGrant: boolean }> {
  const res = await gatewayFetch('user', 'end-grant', { auth: await billingAuth(), method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    throw new Error(data.error || 'Could not end the complimentary access.');
  }
  return { plan: data.plan || 'free', endedGrant: data.endedGrant === true };
}
